import StatusBadge from "@/components/common/StatusBadge";
import UserAvatar from "@/components/common/UserAvatar";
import { getAuditActionCategory, humanizeAuditAction } from "@/utils/organization.utils";
import type { AuditEvent } from "@/types/audit.types";

interface AuditLogRowProps {
  row: AuditEvent;
  badgeClass?: string;
}

export default function AuditLogRow({ row, badgeClass }: AuditLogRowProps) {
  const actor = row.actor_name || row.actor_email || "System";
  const formatValue = (v: unknown) => (typeof v === "object" ? JSON.stringify(v) : String(v));
  const payloadEntries = row.payload
    ? Object.entries(row.payload).filter(([, v]) => v != null && !["", "{}", "[]"].includes(formatValue(v).trim()))
    : [];
  return (
    <div
      className="flex gap-3 border-b border-dash-border px-4 py-4 last:border-b-0 sm:gap-4 sm:px-6 lg:items-center"
      data-testid={`audit-row-${row.id}`}
    >
      <UserAvatar name={actor} src={row.photo_url} className="size-9 shrink-0" fallbackClassName="text-[11px] font-bold" />

      <div className="flex min-w-0 flex-1 flex-col gap-2 lg:flex-row lg:items-center lg:gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-dash-ink lg:truncate">
            <span className="font-semibold">{actor}</span>{" "}
            <span className="text-dash-muted">{humanizeAuditAction(row.action)}</span>
            {row.target_id && <span className="break-all text-dash-muted"> · {row.target_type} {row.target_id}</span>}
          </p>
          {payloadEntries.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-dash-faint lg:flex-nowrap lg:truncate">
              {payloadEntries.slice(0, 4).map(([k, v]) => (
                <span key={k} className="lg:mr-3"><span className="text-dash-placeholder">{k}:</span> {formatValue(v).slice(0, 60)}</span>
              ))}
            </div>
          )}
          {row.actor_email && row.actor_email !== row.actor_name && (
            <div className="mt-0.5 truncate text-xs text-dash-faint">{row.actor_email}</div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 lg:shrink-0 lg:flex-nowrap lg:gap-4">
          <StatusBadge tone={badgeClass} className="whitespace-nowrap text-[10px] font-semibold" data-testid={`audit-action-${row.id}`}>
            {getAuditActionCategory(row.action)}
          </StatusBadge>
          <span className="whitespace-nowrap text-xs text-dash-faint">
            {new Date(row.at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
      </div>
    </div>
  );
}
